import {
  withJsonFormsControlProps,
  withJsonFormsEnumProps,
  withJsonFormsOneOfEnumProps,
  withTranslateProps,
} from "@jsonforms/react";
import React, { useState } from "react";
import * as MaterialRenderers from "@jsonforms/material-renderers";
import FixedTextRenderer, { fixedTextTester } from "./FixedTextRenderer";
import TextAreaRenderer, { textAreaTester } from "./TextAreaRenderer";
import CustomMaterialHorizontalLayout, {
  customMaterialHorizontalLayoutTester,
} from "./CustomMaterialHorizontalLayout.jsx";
import MaterialOneOfRadioGroupControl, {
  materialOneOfRadioGroupControlTester,
} from "./readOnlyRenderers/MaterialOneOfRadioGroupControl.jsx";
import MaterialTextControl, {
  materialTextControlTester,
} from "./readOnlyRenderers/MaterialTextControl.jsx";
import ReadOnlyTextAreaRenderer, {
  textAreaTester as readOnlyTextAreaTester,
} from "./readOnlyRenderers/TextAreaRenderer.jsx";
import MaterialIntegerControl, {
  materialIntegerControlTester,
} from "./readOnlyRenderers/MaterialIntegerControl.jsx";
import MaterialEnumControl, {
  materialEnumControlTester,
} from "./readOnlyRenderers/MaterialEnumControl.jsx";

/**
 * @typedef {Object} Props
 * @property {Object} config - Render config
 * @property {Object} uischema - UI Schema for rendering
 * @property {string} errors - Error messages
 * @property {React.ComponentType} wrappedControl - Form control
 */

/**
 * Component ControlWithTouched
 * @param {Props} props
 */
export function ControlWithTouched(props) {
  const [touched, setTouched] = useState(false);
  const WrappedControl = props.wrappedControl;
  return (
    <div onBlur={() => setTouched(true)}>
      <WrappedControl {...unwrapProps(props, touched)} />
    </div>
  );
}

/**
 * Unwraps props for wrappedControl.
 * @param {Props} props
 * @param {boolean} touched - Indicates, weather control was touched.
 * @returns {Object} Updated props for wrappedControl.
 */
function unwrapProps(props, touched) {
  const { errors, config, uischema } = props;
  const { forceShow } = { ...config, ...uischema.options };
  const showErrors = touched || forceShow;
  const result = { ...props, errors: showErrors ? errors : "" };
  delete result.wrappedControl;
  return result;
}

export const wrapperFunctions = {
  /**
   * @param {React.ComponentType} c
   * @returns {React.ComponentType}
   */
  MaterialEnumControl: (c) => withJsonFormsEnumProps(c, false),

  /**
   * @param {React.ComponentType} c
   * @returns {React.ComponentType}
   */
  MaterialOneOfEnumControl: (c) =>
    withJsonFormsOneOfEnumProps(withTranslateProps(React.memo(c)), false),

  /**
   * @param {React.ComponentType} c
   * @returns {React.ComponentType}
   */
  MaterialOneOfRadioGroupControl: (c) => withJsonFormsOneOfEnumProps(c),
};

/**
 * @typedef {Object} RendererRegistryEntry
 * @property {Function} tester - Tester function.
 * @property {Function} renderer - Renderer function.
 */

/**
 * Wraps renderer with additional functionality.
 * @param {RendererRegistryEntry} entry - Entry in renderers registry.
 * @returns {RendererRegistryEntry} Updated renderer entry.
 */
export function wrapRendererRegistry(entry) {
  const tester = entry.tester;
  let renderer = entry.renderer;
  Object.entries(MaterialRenderers.Unwrapped).some(([key, wrappedControl]) => {
    const otherTester = MaterialRenderers[`m${key.substring(1)}Tester`];
    const match = tester === otherTester;
    if (match) {
      const wrapperFunction =
        wrapperFunctions[key] || withJsonFormsControlProps;
      renderer = wrapperFunction((props) => (
        <ControlWithTouched {...{ ...props, wrappedControl }} />
      ));
    }
    return match;
  });
  return { renderer, tester };
}

export const materialRenderers = [
  ...MaterialRenderers.materialRenderers.map(wrapRendererRegistry),
  {
    tester: customMaterialHorizontalLayoutTester,
    renderer: CustomMaterialHorizontalLayout,
  },
  { tester: fixedTextTester, renderer: FixedTextRenderer },
  { tester: textAreaTester, renderer: TextAreaRenderer },
];

export const readonlyRenderers = [
  { tester: readOnlyTextAreaTester, renderer: ReadOnlyTextAreaRenderer },
  {
    tester: materialTextControlTester,
    renderer: MaterialTextControl,
  },
  {
    tester: materialIntegerControlTester,
    renderer: MaterialIntegerControl,
  },
  {
    tester: materialEnumControlTester,
    renderer: MaterialEnumControl,
  },
  {
    tester: materialOneOfRadioGroupControlTester,
    renderer: MaterialOneOfRadioGroupControl,
  },
];

export const materialCells = MaterialRenderers.materialCells;
